/**
 * Placement maths for portalled overlays (tooltips, menus).
 *
 * Everything is expressed in viewport coordinates and consumed with
 * `position: fixed`, which keeps scroll containers and transformed ancestors
 * out of the picture entirely.
 */
export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';

export interface AnchorRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

export interface FloatingSize {
  readonly width: number;
  readonly height: number;
}

export interface Placement {
  readonly top: number;
  readonly left: number;
  readonly side: PopoverSide;
}

export interface PlacementOptions {
  side?: PopoverSide;
  align?: PopoverAlign;
  /** Distance between anchor and floating element. */
  gap?: number;
  /** Minimum distance kept from every viewport edge. */
  padding?: number;
  viewport?: FloatingSize;
}

const OPPOSITE: Record<PopoverSide, PopoverSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function currentViewport(): FloatingSize {
  if (typeof window === 'undefined') return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function freeSpace(
  side: PopoverSide,
  anchor: AnchorRect,
  viewport: FloatingSize,
  gap: number,
): number {
  if (side === 'top') return anchor.top - gap;
  if (side === 'bottom') return viewport.height - (anchor.top + anchor.height) - gap;
  if (side === 'left') return anchor.left - gap;
  return viewport.width - (anchor.left + anchor.width) - gap;
}

function crossAxis(
  anchorStart: number,
  anchorSize: number,
  floatingSize: number,
  align: PopoverAlign,
): number {
  if (align === 'start') return anchorStart;
  if (align === 'end') return anchorStart + anchorSize - floatingSize;
  return anchorStart + anchorSize / 2 - floatingSize / 2;
}

/**
 * Resolves a concrete position for `floating` relative to `anchor`, flipping to
 * the opposite side when the preferred one cannot fit, then clamping so the
 * element never leaves the viewport.
 */
export function computePlacement(
  anchor: AnchorRect,
  floating: FloatingSize,
  options: PlacementOptions = {},
): Placement {
  const { side = 'bottom', align = 'center', gap = 8, padding = 8 } = options;
  const viewport = options.viewport ?? currentViewport();

  const vertical = side === 'top' || side === 'bottom';
  const needed = vertical ? floating.height : floating.width;
  const preferredSpace = freeSpace(side, anchor, viewport, gap);
  const oppositeSpace = freeSpace(OPPOSITE[side], anchor, viewport, gap);
  const resolved =
    preferredSpace < needed && oppositeSpace > preferredSpace ? OPPOSITE[side] : side;

  let top: number;
  let left: number;
  if (resolved === 'top') {
    top = anchor.top - gap - floating.height;
    left = crossAxis(anchor.left, anchor.width, floating.width, align);
  } else if (resolved === 'bottom') {
    top = anchor.top + anchor.height + gap;
    left = crossAxis(anchor.left, anchor.width, floating.width, align);
  } else if (resolved === 'left') {
    left = anchor.left - gap - floating.width;
    top = crossAxis(anchor.top, anchor.height, floating.height, align);
  } else {
    left = anchor.left + anchor.width + gap;
    top = crossAxis(anchor.top, anchor.height, floating.height, align);
  }

  return {
    side: resolved,
    left: clamp(left, padding, Math.max(padding, viewport.width - floating.width - padding)),
    top: clamp(top, padding, Math.max(padding, viewport.height - floating.height - padding)),
  };
}
